import type { ContractItemsQueryParams, ContractItemsResponse } from './types';

import {
  keepPreviousData,
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { useEffect } from 'react';

import { type UUID } from '~/types/common';

import { queryKeysContract } from '../queryKeys';

import { fetchContractItems } from './fetchContractItems';

/**
 * Generates the query options for the contract items query so they can be shared between the useQuery hook and the prefetching.
 *
 * @param contractId - The ID of the contract to fetch items for.
 * @param queryParams - The query parameters for pagination.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getContractItemsQueryOptions = <TData = ContractItemsResponse>({
  contractId,
  queryParams,
  options,
}: {
  contractId: UUID | undefined;
  queryParams: Partial<ContractItemsQueryParams>;
  options?: Omit<
    UseQueryOptions<ContractItemsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: contractId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchContractItems(contractId, queryParams, { signal })
      : skipToken,
    queryKey: queryKeysContract.getItems(contractId ?? '', queryParams),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for getting the line items for a contract with given query parameters.
 * Handles pagination and prefetches the next page of items for better performance.
 *
 * @param contractId - The ID of the contract to fetch items for.
 * @param queryParams - The query parameters for pagination.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/list_contract_items_contracts__contract_id__items_get
 */
export const useQueryContractItems = (
  contractId: UUID | undefined,
  queryParams: Partial<ContractItemsQueryParams> = {},
  options?: Omit<
    UseQueryOptions<ContractItemsResponse, Error, ContractItemsResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryClient = useQueryClient();

  // Prefetch the next page for better UX
  useEffect(() => {
    if (!contractId) {
      return;
    }

    const limit = queryParams.limit ?? 100;
    const offset = queryParams.offset ?? 0;

    void queryClient.prefetchQuery(
      getContractItemsQueryOptions({
        contractId,
        options,
        queryParams: {
          ...queryParams,
          offset: offset + limit,
        },
      }),
    );
  }, [
    contractId,
    JSON.stringify(queryParams),
    JSON.stringify(options),
    queryClient,
  ]);

  return useQuery({
    ...getContractItemsQueryOptions<ContractItemsResponse>({
      contractId,
      options,
      queryParams,
    }),
    placeholderData: keepPreviousData,
  });
};
